import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface TabNote {
  id: string;
  tab_name: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
}

export interface TabNoteListResponse {
  notes: TabNote[];
  total: number;
}

export interface TabNoteCreate {
  tab_name: string;
  content: string;
}

export interface TabNoteUpdate {
  content: string;
}

@Injectable({
  providedIn: 'root'
})
export class TabNotesService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Get all tab notes (Admin only)
   */
  getAllTabNotes(): Observable<TabNoteListResponse> {
    return this.http.get<TabNoteListResponse>(
      `${this.API_URL}/admin/tab-notes/`
    );
  }

  /**
   * Get a specific tab note by tab name (Admin only)
   */
  getTabNote(tabName: string): Observable<TabNote> {
    return this.http.get<TabNote>(
      `${this.API_URL}/admin/tab-notes/${tabName}`
    );
  }

  /**
   * Get a specific tab note by tab name (Public - authenticated users)
   */
  getTabNotePublic(tabName: string): Observable<TabNote> {
    return this.http.get<TabNote>(
      `${this.API_URL}/admin/tab-notes/public/${tabName}`
    );
  }

  /**
   * All tab notes in one response (authenticated users) — for Glossary tab.
   * Not scoped to project or analysis version.
   */
  getGlossaryTabNotes(): Observable<TabNoteListResponse> {
    return this.http.get<TabNoteListResponse>(
      `${this.API_URL}/admin/tab-notes/glossary`
    );
  }

  /**
   * Create a new tab note (Admin only)
   */
  createTabNote(noteData: TabNoteCreate): Observable<TabNote> {
    return this.http.post<TabNote>(
      `${this.API_URL}/admin/tab-notes/`,
      noteData
    );
  }

  /**
   * Update an existing tab note (Admin only)
   */
  updateTabNote(tabName: string, noteData: TabNoteUpdate): Observable<TabNote> {
    return this.http.put<TabNote>(
      `${this.API_URL}/admin/tab-notes/${tabName}`,
      noteData
    );
  }

  /**
   * Delete a tab note (Admin only)
   */
  deleteTabNote(tabName: string): Observable<{ message: string; tab_name: string }> {
    return this.http.delete<{ message: string; tab_name: string }>(
      `${this.API_URL}/admin/tab-notes/${tabName}`
    );
  }
}
